import { AxiosResponse } from 'axios';
import {
  BatchImportPreview,
  BatchImportResult,
  CreateEmployeePaySlipData,
  PayslipPending,
  SignaturePaySlipData,
} from '../@types/employee-payslip';
import {
  EmployeePayslip,
  EmployeePayslipMin,
} from '../models/employee-payslip';
import { api } from '../providers/base-api';

export async function createEmployeePayslipRequest(
  data: CreateEmployeePaySlipData,
): Promise<AxiosResponse<EmployeePayslip>> {
  const response = await api.post<EmployeePayslip>(`/employee-payslip`, data);

  return response;
}

export async function listEmployeePayslipByUserRequest(
  employeeId: number,
): Promise<AxiosResponse<EmployeePayslip[]>> {
  const response = await api.get<EmployeePayslip[]>(
    `/employee-payslip/${employeeId}`,
  );

  return response;
}

export async function findEmployeePayslipByHashCodeRequest(
  hashCode: string,
): Promise<AxiosResponse<EmployeePayslipMin>> {
  const response = await api.get<EmployeePayslipMin>(
    `/employee-payslip/hash/${hashCode}`,
  );

  return response;
}

export async function verifyCpfRequest(
  hashCode: string,
  cpf: string,
): Promise<AxiosResponse<void>> {
  return api.post<void>(`/employee-payslip/verify-cpf/${hashCode}`, { cpf });
}

export async function sendVerificationCodeRequest(
  hashCode: string,
): Promise<AxiosResponse<void>> {
  return api.post<void>(`/employee-payslip/send-code/${hashCode}`);
}

export async function verifyCodeRequest(
  hashCode: string,
  verificationCode: string,
): Promise<AxiosResponse<void>> {
  return api.post<void>(`/employee-payslip/verify-code/${hashCode}`, {
    verificationCode,
  });
}

export async function signatureEmployeePayslipRequest(
  data: SignaturePaySlipData,
): Promise<AxiosResponse<void>> {
  const response = await api.post<void>(
    `/employee-payslip/signature/${data.hashCode}`,
    data,
  );

  return response;
}

export async function previewBatchPayslipRequest(
  file: File,
): Promise<AxiosResponse<BatchImportPreview>> {
  const formData = new FormData();
  formData.append('file', file);

  return api.post<BatchImportPreview>(
    '/employee-payslip/import-batch/preview',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
}

export async function importBatchPayslipRequest(
  file: File,
  overrides: Record<number, number> = {},
  referenceMonth?: string,
  mirrorPageIndexes?: number[],
): Promise<AxiosResponse<BatchImportResult>> {
  const formData = new FormData();
  formData.append('file', file);
  if (Object.keys(overrides).length > 0) {
    formData.append('overrides', JSON.stringify(overrides));
  }
  if (referenceMonth) {
    formData.append('referenceMonth', referenceMonth);
  }
  if (mirrorPageIndexes !== undefined) {
    formData.append('mirrorPageIndexes', JSON.stringify(mirrorPageIndexes));
  }

  return api.post<BatchImportResult>(
    '/employee-payslip/import-batch/confirm',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
}

export async function regeneratePayslipLinkRequest(
  id: number,
): Promise<AxiosResponse<{ url: string }>> {
  return api.post<{ url: string }>(`/employee-payslip/${id}/regenerate-link`);
}

export async function deletePayslipRequest(
  id: number,
): Promise<AxiosResponse<void>> {
  return api.delete<void>(`/employee-payslip/${id}`);
}

export async function getPayslipPendingRequest(): Promise<
  AxiosResponse<PayslipPending>
> {
  return api.get<PayslipPending>('/employee-payslip/pending');
}

export async function searchPayslipEmployeesRequest(
  term: string,
): Promise<AxiosResponse<{ id: number; name: string }[]>> {
  return api.get<{ id: number; name: string }[]>(
    '/employee-payslip/import-batch/search-employees',
    { params: { q: term } },
  );
}
